import { useEffect, useRef, useState } from 'react'
import { clsx } from 'clsx'

interface AddressComponents {
  address: string
  city: string
  state: string
  zip: string
}

interface AddressAutocompleteProps {
  value: string
  onChange: (value: string) => void
  onAddressSelect: (components: AddressComponents) => void
  placeholder?: string
  className?: string
  error?: boolean
}

// Load Google Places script
let googleScriptLoaded = false
let googleScriptLoading = false
const loadCallbacks: (() => void)[] = []

function loadGooglePlacesScript(apiKey: string): Promise<void> {
  return new Promise((resolve) => {
    if (googleScriptLoaded) {
      resolve()
      return
    }

    loadCallbacks.push(resolve)

    if (googleScriptLoading) {
      return
    }

    googleScriptLoading = true

    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`
    script.async = true
    script.defer = true
    script.onload = () => {
      googleScriptLoaded = true
      loadCallbacks.forEach((cb) => cb())
      loadCallbacks.length = 0
    }
    document.head.appendChild(script)
  })
}

function parseAddressComponents(place: google.maps.places.PlaceResult): AddressComponents {
  const components: AddressComponents = {
    address: '',
    city: '',
    state: '',
    zip: '',
  }

  if (!place.address_components) {
    return components
  }

  let streetNumber = ''
  let route = ''

  for (const component of place.address_components) {
    const types = component.types

    if (types.includes('street_number')) {
      streetNumber = component.long_name
    } else if (types.includes('route')) {
      route = component.long_name
    } else if (types.includes('locality')) {
      components.city = component.long_name
    } else if (types.includes('administrative_area_level_1')) {
      components.state = component.short_name
    } else if (types.includes('postal_code')) {
      components.zip = component.long_name
    }
  }

  components.address = streetNumber ? `${streetNumber} ${route}` : route

  return components
}

export function AddressAutocomplete({
  value,
  onChange,
  onAddressSelect,
  placeholder = 'Start typing an address...',
  className,
  error,
}: AddressAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null)
  const [isLoaded, setIsLoaded] = useState(false)

  const apiKey = import.meta.env.VITE_GOOGLE_PLACES_API_KEY

  useEffect(() => {
    if (!apiKey) {
      console.warn('Google Places API key not configured')
      return
    }

    loadGooglePlacesScript(apiKey).then(() => {
      setIsLoaded(true)
    })
  }, [apiKey])

  useEffect(() => {
    if (!isLoaded || !inputRef.current || autocompleteRef.current) {
      return
    }

    const autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
      types: ['address'],
      componentRestrictions: { country: 'us' },
      fields: ['address_components', 'formatted_address'],
    })

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace()
      if (place.address_components) {
        const components = parseAddressComponents(place)
        onChange(components.address)
        onAddressSelect(components)
      }
    })

    autocompleteRef.current = autocomplete

    return () => {
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current)
      }
    }
  }, [isLoaded, onChange, onAddressSelect])

  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={clsx(
        'block w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
        error && 'border-danger-300 focus:border-danger-500 focus:ring-danger-500',
        className
      )}
    />
  )
}
